import { useContext, useEffect } from 'react';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import PropTypes from 'prop-types';
import { getPaymentTransactionErrorMessage } from '../../utils';
import { PAYMENT_METHOD } from 'components/common/constants';
import i18nContext from 'components/i18n-context';
import getCommissionRequestModel from 'components/UnapprovedPayments/models/getCommissionRequestModel';
import { CURRENCIES } from 'services/currenciesConstant';
import { amountFormattedValue, getErrorMessageForAlert, isCryptoProvider } from 'services/utils';
import Alert from 'uikit/Alert/Alert';
import Button from 'uikit/Button/Button';
import './PaymentConfirmation.scss';

const PaymentConfirmation = ({
  transaction,
  onEdit,
  onConfirm,
  isLoading,
  customerNumber,
  setSelectedTransaction,
  paymentStore
}) => {
  const i18n = useContext(i18nContext);
  const isCrypto = isCryptoProvider(transaction?.transferProvider);

  useEffect(() => {
    if (transaction) {
      const fetchCommission = async () => {
        const requestData = getCommissionRequestModel(transaction);

        await paymentStore.getPreparedTransactionCommission(
          customerNumber,
          requestData,
          transaction.id,
          setSelectedTransaction
        );
      };

      fetchCommission();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEdit = () => {
    if (onEdit) {
      onEdit(transaction.id);
    }
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
  };

  const renderDetailRow = (label, value, valueClassName) => {
    if (!value) {
      return null;
    }

    return (
      <div className={'payment-detail-row'}>
        <div className={'payment-detail-label'}>{label}</div>
        <div className={`payment-detail-value ${valueClassName}`}>{value}</div>
      </div>
    );
  };

  return (
    <div className={'for-approval-payment-confirmation-container'}>
      <div className={'for-approval-payment-confirmation-card'}>
        <div className={'payment-section'}>
          <h3 className={'section-title'}>
            {i18n.getMessage('unapprovedPayments.paymentConfirmation.paymentDetails')}
          </h3>
          {renderDetailRow(
            i18n.getMessage('unapprovedPayments.paymentConfirmation.amount'),
            //eslint-disable-next-line max-len
            `${CURRENCIES[transaction.currency]?.symbol || transaction.currency} ${amountFormattedValue(transaction.amount, isCrypto ? 8 : 2)}`,
            'amount-highlight'
          )}
          {renderDetailRow(
            i18n.getMessage('unapprovedPayments.paymentConfirmation.commission'),
            //eslint-disable-next-line max-len
            `${CURRENCIES[transaction.currency]?.symbol || transaction.currency} ${amountFormattedValue(transaction.commission, isCrypto ? 8 : 2)}`
          )}
        </div>

        <div className={'section-divider'} />

        <div className={'payment-section'}>
          <h3 className={'section-title'}>
            {i18n.getMessage('unapprovedPayments.paymentConfirmation.recipientDetails')}
          </h3>
          {isCrypto ? (
            renderDetailRow(
              i18n.getMessage('unapprovedPayments.paymentConfirmation.recipientWalletAddress'),
              transaction.transactionData?.recipient?.walletAddress
            )
          ) : (
            <>
              {!transaction.transactionData.transaction_recipient.requisites.iban &&
              transaction.paymentMethod !== PAYMENT_METHOD.SWIFT ? (
                <>
                  {renderDetailRow(
                    i18n.getMessage('unapprovedPayments.paymentConfirmation.accountNumber'),
                    transaction.transactionData.transaction_recipient.requisites.account_number
                  )}
                  {renderDetailRow(
                    i18n.getMessage('unapprovedPayments.paymentConfirmation.sortCode'),
                    transaction.transactionData.transaction_recipient.requisites.sort_code
                  )}
                </>
              ) : (
                renderDetailRow(
                  i18n.getMessage('unapprovedPayments.paymentConfirmation.recipientIban'),
                  transaction.transactionData.transaction_recipient.requisites.iban
                )
              )}
              {renderDetailRow(
                i18n.getMessage('unapprovedPayments.paymentConfirmation.bankName'),
                transaction.transactionData.transaction_recipient.requisites.bank_name
              )}
              {renderDetailRow(
                i18n.getMessage('unapprovedPayments.paymentConfirmation.bankCountry'),
                transaction.transactionData.transaction_recipient.requisites.bank_country
              )}
              {renderDetailRow(
                i18n.getMessage('unapprovedPayments.paymentConfirmation.recipientBicSwift'),
                transaction.transactionData.transaction_recipient.requisites.bic
              )}
              {renderDetailRow(
                i18n.getMessage('unapprovedPayments.paymentConfirmation.recipientName'),
                transaction.counterpartyName
              )}
              {renderDetailRow(
                i18n.getMessage('unapprovedPayments.paymentConfirmation.registrationNumber'),
                transaction.transactionData.transaction_recipient.registration_number
              )}
              {renderDetailRow(
                i18n.getMessage('unapprovedPayments.paymentConfirmation.recipientCity'),
                transaction.transactionData.transaction_recipient.beneficiary_city
              )}
              {renderDetailRow(
                i18n.getMessage('unapprovedPayments.paymentConfirmation.recipientCountry'),
                transaction.transactionData.transaction_recipient.beneficiary_country
              )}
              {renderDetailRow(
                i18n.getMessage('unapprovedPayments.paymentConfirmation.recipientPostalCode'),
                transaction.transactionData.transaction_recipient.beneficiary_post_code
              )}
              {renderDetailRow(
                i18n.getMessage('unapprovedPayments.paymentConfirmation.recipientAddress'),
                transaction.transactionData.transaction_recipient.beneficiary_address
              )}
            </>
          )}
        </div>
      </div>

      <div className={'error-message-container'}>
        {paymentStore.isErrorForPreparedTransaction(transaction.id) && (
          <span className={'error-message'}>
            {getPaymentTransactionErrorMessage(i18n, paymentStore, transaction.id)}
          </span>
        )}
        {!paymentStore.isErrorForPreparedTransaction(transaction.id) && paymentStore.preparedTransactionsError && (
          <Alert
            type={'warning'}
            className={'exchange-wrapper-alert'}
            message={getErrorMessageForAlert(i18n, paymentStore.preparedTransactionsError)}
          />
        )}
      </div>

      <div className={'payment-actions'}>
        <Button type={'outline'} onClick={handleEdit} isLoading={isLoading}>
          {i18n.getMessage('unapprovedPayments.paymentConfirmation.button.editTransfer')}
        </Button>
        <Button type={'primary'} onClick={handleConfirm} isLoading={isLoading}>
          {i18n.getMessage('unapprovedPayments.paymentConfirmation.button.confirm')}
        </Button>
      </div>
    </div>
  );
};

PaymentConfirmation.propTypes = {
  transaction: PropTypes.shape({
    id: PropTypes.string,
    accountNumber: PropTypes.string,
    walletNumber: PropTypes.string,
    amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    commission: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.oneOf([null])]),
    currency: PropTypes.string,
    paymentMethod: PropTypes.string,
    transferProvider: PropTypes.string,
    senderName: PropTypes.string,
    senderAccount: PropTypes.string,
    counterpartyName: PropTypes.string,
    comment: PropTypes.string,
    transactionData: PropTypes.object,
    createdAt: PropTypes.string,
    updatedAt: PropTypes.string
  }),
  onEdit: PropTypes.func,
  onConfirm: PropTypes.func,
  setSelectedTransaction: PropTypes.func,
  isLoading: PropTypes.bool,
  customerNumber: PropTypes.string,
  paymentStore: MobXPropTypes.observableObject,
  error: PropTypes.any
};

export default inject((stores) => ({
  paymentStore: stores.paymentStore
}))(observer(PaymentConfirmation));
